@layer tbw-plugins {
  /* Enable container query units (cqi) so .tbw-pinned-rows can size itself
   * to the scroll area's visible width even when the footer is wider. */
  .tbw-scroll-area {
    container-type: inline-size;
  }

  .tbw-footer {
    /* Use normal flow (not sticky) so footer takes space in flex layout
     * rather than overlapping content. Parent .tbw-scroll-area is flex column,
     * so this footer becomes a flex child that shrinks the rows area. */
    flex-shrink: 0;
    z-index: var(--tbw-z-layer-pinned-rows, 20);
    background: var(--tbw-color-panel-bg);
    /* Span the full scrollable width so sticky children have room to slide
     * within the scroll container. */
    min-width: fit-content;
  }

  /* Top-side counterpart to .tbw-footer used by slot-mode (issue #255).
   * Holds top pinned-row slots in declared order. Sits between the header
   * and the rows area; same flex/width semantics as the footer so sticky
   * children behave identically. */
  .tbw-header-pinned {
    flex-shrink: 0;
    z-index: var(--tbw-z-layer-pinned-rows, 20);
    background: var(--tbw-color-panel-bg);
    min-width: fit-content;
  }

  .tbw-pinned-rows {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--tbw-button-padding, var(--tbw-spacing-md, 0.5rem) var(--tbw-spacing-lg, 0.75rem));
    background: var(--tbw-pinned-rows-bg, var(--tbw-color-panel-bg));
    border-top: 1px solid var(--tbw-pinned-rows-border, var(--tbw-color-border));
    font-size: var(--tbw-font-size-xs, 0.75rem);
    color: var(--tbw-pinned-rows-color, var(--tbw-color-fg-muted));
    min-height: 32px;
    box-sizing: border-box;
    /* Sticky: lock the info bar to the left edge during horizontal scroll.
     * Width uses container query units (100cqi = scroll area's visible width)
     * so the bar matches the viewport, not the full scrollable width. */
    position: sticky;
    left: 0;
    min-width: 0;
    width: 100cqi;
  }
  /* Slot-mode (issue #255) can stack multiple .tbw-pinned-rows rows inside the
   * same wrapper. Suppress the duplicate border only between adjacent panel
   * rows so they read as one continuous bar. Mixed pinned/aggregation stacks
   * still need their separator (the info bar's own border-top in legacy mode
   * is the divider between aggregation and info bar). */
  .tbw-pinned-rows + .tbw-pinned-rows {
    border-top: 0;
  }
  .tbw-pinned-rows-left,
  .tbw-pinned-rows-center,
  .tbw-pinned-rows-right {
    display: flex;
    align-items: center;
    gap: var(--tbw-spacing-xl, 1rem);
  }
  .tbw-pinned-rows-left {
    justify-content: flex-start;
  }
  .tbw-pinned-rows-center {
    justify-content: center;
    flex: 1;
  }
  .tbw-pinned-rows-right {
    justify-content: flex-end;
  }
  .tbw-status-panel {
    white-space: nowrap;
  }

  .tbw-aggregation-rows {
    min-width: fit-content;
    background: var(--tbw-aggregation-bg, var(--tbw-color-header-bg));
  }
  .tbw-aggregation-rows-top {
    border-bottom: 1px solid var(--tbw-aggregation-border, var(--tbw-color-border));
  }
  .tbw-aggregation-rows-bottom {
    border-top: 1px solid var(--tbw-aggregation-border, var(--tbw-color-border));
  }
  .tbw-aggregation-row {
    display: grid;
    grid-template-columns: var(--tbw-column-template);
    font-size: var(--tbw-aggregation-font-size, 0.8em);
    font-weight: var(--tbw-aggregation-font-weight, 600);
    position: relative;
    /* Inherit background from .tbw-aggregation-rows so children using
     * background: inherit get an opaque color for z-order layering. */
    background: inherit;
  }
  /* Sticky label in per-column mode: sticks to the left edge during horizontal
   * scroll. Placed in grid-row 1 / column 1 so it overlaps the first cell.
   * Value cells (:not(:empty)) have higher z-index to cover the label. */
  .tbw-aggregation-row > .tbw-aggregation-label {
    position: sticky;
    left: 0;
    grid-row: 1;
    grid-column: 1;
    display: flex;
    align-items: center;
    padding: var(--tbw-cell-padding, 0.125rem 0.5rem);
    background: inherit;
    z-index: 1;
    pointer-events: none;
  }
  /* Pin the first cell to column 1 so it shares the grid area with the sticky
   * label. Without this, the label's explicit grid-column: 1 causes
   * auto-placed cells to shift one column to the right. */
  .tbw-aggregation-row > .tbw-aggregation-cell:first-child {
    grid-column: 1;
    grid-row: 1;
  }
  /* Value cells paint above the sticky label to create the "push-out" effect */
  .tbw-aggregation-cell:not(:empty) {
    position: relative;
    z-index: 2;
    background: inherit;
  }
  .tbw-aggregation-cell {
    padding: var(--tbw-cell-padding, 0.125rem 0.5rem);
    min-height: var(--tbw-row-height, 1.75rem);
    display: block;
    align-items: center;
    align-content: center;
    border-right: 1px solid var(--tbw-color-border-cell);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: var(--tbw-cell-white-space, nowrap);
  }
  .tbw-aggregation-cell:last-child {
    border-right: 0;
  }
  .tbw-aggregation-cell-full {
    grid-column: 1 / -1;
    border-right: 0;
    display: flex;
    align-items: center;
    gap: var(--tbw-spacing-lg, 0.75rem);
  }
  .tbw-aggregation-label {
    white-space: nowrap;
  }
  .tbw-aggregation-aggregates {
    display: flex;
    align-items: center;
    gap: var(--tbw-spacing-lg, 0.75rem);
    font-weight: normal;
    opacity: 0.85;
  }
  .tbw-aggregation-aggregate {
    white-space: nowrap;
  }
} /* End @layer tbw-plugins */
